import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { API_URL } from '../services/api';

function QuoteAcceptancePage() {
  const { token } = useParams();
  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [signatureBusiness, setSignatureBusiness] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { status: 'ACCEPTED' | 'DECLINED' }
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineRemarks, setDeclineRemarks] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/public/quotes/acceptance/${token}`)
      .then(res => {
        if (res.data.expired) {
          setExpired(true);
          setExpiredMessage(res.data.message);
        } else {
          setQuoteData(res.data);
          setSignatureBusiness(res.data.business_name || '');
        }
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'This link is invalid or has expired.');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters are stable, only re-fetch when token changes
  }, [token]);

  const handleSubmit = async (action) => {
    if (!signatureName.trim()) {
      alert('Please enter your name to proceed.');
      return;
    }
    if (!signatureBusiness.trim()) {
      alert('Please enter the business name to proceed.');
      return;
    }
    if (action === 'decline' && !declineReason) {
      alert('Please select a reason for declining the quote.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/quotes/acceptance/${token}`, {
        action,
        signature_name: signatureName.trim(),
        signature_business: signatureBusiness.trim(),
        decline_reason: action === 'decline' ? declineReason : '',
        decline_remarks: action === 'decline' ? declineRemarks.trim() : '',
      });
      setShowDeclineModal(false);
      setResult({ status: res.data.status });
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeclineModal = () => {
    if (!signatureName.trim() || !signatureBusiness.trim()) {
      alert('Please fill in your name and business name before declining.');
      return;
    }
    setDeclineReason('');
    setDeclineRemarks('');
    setShowDeclineModal(true);
  };

  const buttonsEnabled = signatureName.trim() !== '' && signatureBusiness.trim() !== '';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-[#f4931d]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <img src="/logo.png" alt="PMP" className="h-16 mx-auto mb-6" onError={e => e.target.style.display = 'none'} />
          <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
          <p className="text-gray-600" data-testid="acceptance-error">{error}</p>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <img src="/logo.png" alt="PMP" className="h-16 mx-auto mb-6" onError={e => e.target.style.display = 'none'} />
          <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
          <p className="text-gray-600" data-testid="acceptance-expired">{expiredMessage}</p>
        </div>
      </div>
    );
  }

  if (result) {
    const isAccepted = result.status === 'ACCEPTED';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <img src="/logo.png" alt="PMP" className="h-16 mx-auto mb-6" onError={e => e.target.style.display = 'none'} />
          {isAccepted ? (
            <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
          ) : (
            <XCircle size={48} className="mx-auto text-red-500 mb-4" />
          )}
          <div className={`p-4 rounded ${isAccepted ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="acceptance-result">
            Quote has been {result.status}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="quote-acceptance-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Logo */}
        <div className="text-center mb-6">
          <img src="/logo.png" alt="Project Maintenance Perth" className="h-20 mx-auto" onError={e => e.target.style.display = 'none'} />
        </div>

        {/* Terms of Trade */}
        <h3 className="text-lg font-bold text-gray-500 mb-2">Terms of trade</h3>
        <div className="bg-white border-2 border-[#f4931d] rounded-sm p-6 mb-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <TermsOfTrade />

          {/* Signature section at bottom of terms */}
          <div className="mt-6 text-sm">
            I{' '}
            <input
              type="text"
              value={signatureName}
              onChange={e => setSignatureName(e.target.value)}
              placeholder="(Customer)"
              className="border-0 border-b border-gray-300 focus:border-[#178c98] outline-none px-2 py-1 text-center w-52 transition-colors text-sm"
              data-testid="signature-name"
            />
            {' '}of{' '}
            <input
              type="text"
              value={signatureBusiness}
              onChange={e => setSignatureBusiness(e.target.value)}
              className="border-0 border-b border-gray-300 focus:border-[#178c98] outline-none px-2 py-1 text-center w-52 transition-colors text-sm"
              data-testid="signature-business"
            />
            {' '}have read and accept the Terms of Trade as outlined in this document.
          </div>
        </div>

        {/* Accept / Decline buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={openDeclineModal}
            disabled={!buttonsEnabled || submitting}
            className="w-full py-2.5 px-4 text-sm font-medium rounded bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="decline-btn"
          >
            Decline
          </button>
          <button
            onClick={() => handleSubmit('accept')}
            disabled={!buttonsEnabled || submitting}
            className="w-full py-2.5 px-4 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="accept-btn"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            Accept
          </button>
        </div>
      </div>

      {/* Decline reason modal */}
      {showDeclineModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="decline-modal"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-1 text-gray-900">Please tell us why</h3>
            <p className="text-sm text-gray-500 mb-4">Your feedback helps us improve. Required before declining.</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
            <select
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#178c98] mb-3"
              data-testid="decline-reason-select"
            >
              <option value="">Select a reason...</option>
              <option value="No response">No response</option>
              <option value="Pricing issue">Pricing issue</option>
              <option value="Lost to competitor">Lost to competitor</option>
              <option value="Project cancelled">Project cancelled</option>
              <option value="Project on hold / postponed">Project on hold / postponed</option>
              <option value="Scope changed">Scope changed</option>
              <option value="Insufficient capacity / timing">Insufficient capacity / timing</option>
              <option value="Funding / budget not approved">Funding / budget not approved</option>
              <option value="Client went in-house">Client went in-house</option>
              <option value="Other reason">Other reason</option>
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional remarks {declineReason === 'Other reason' ? '*' : '(optional)'}
            </label>
            <textarea
              value={declineRemarks}
              onChange={(e) => setDeclineRemarks(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#178c98]"
              rows={3}
              placeholder={declineReason === 'Other reason' ? 'Please describe your reason...' : 'Any further detail (optional)'}
              data-testid="decline-remarks-textarea"
            />

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowDeclineModal(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                data-testid="decline-cancel-btn"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (!declineReason) {
                    alert('Please select a reason for declining.');
                    return;
                  }
                  if (declineReason === 'Other reason' && !declineRemarks.trim()) {
                    alert('Please describe your reason.');
                    return;
                  }
                  handleSubmit('decline');
                }}
                disabled={submitting || !declineReason}
                className="px-4 py-2 text-sm font-medium rounded bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="decline-confirm-btn"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Confirm Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function TermsOfTrade() {
  return (
    <div className="text-xs leading-relaxed space-y-4">
      <Section title="1. Definitions">
        <Row num="1.1">The Company means Project Maintenance Perth</Row>
        <Row num="1.2">Customer means the person, or Company buying the goods and services from Project Maintenance Perth</Row>
        <Row num="1.3">Products and/or services mean the products and/or services being purchased by the Customer from the Project Maintenance Perth</Row>
        <Row num="1.4">Contract means the contract between Project Maintenance Perth and the Customer for the purchase of the goods and services.</Row>
        <Row num="1.5">Contract price means the price of goods and services as agreed between the Customer and Project Maintenance Perth</Row>
      </Section>

      <Section title="2. Quotation">
        <Row num="2.1">We will endeavour to provide you with a written quotation within seven (7) days of receiving your request.</Row>
        <Row num="2.2">Project Maintenance Perth quotation shall be deemed to interpret correctly the Customers instructions whether written or oral and the Customer is accordingly advised to exercise care in checking quotations. Any additional work not quoted for shall be carried out at the Customers expense.</Row>
        <Row num="2.3">Quotes are valid for 30 days from date of receipt, unless we agree otherwise.</Row>
        <Row num="2.4">Quotes are non-negotiable unless we choose to offer a discount.</Row>
        <Row num="2.5">All quotes provided must be copied and a signed copy sent back to our office prior to any work commencing.</Row>
      </Section>

      <Section title="3. Price">
        <Row num="3.1">The Price shall be as indicated on invoices provided by Project Maintenance Perth to the Customer in respect of products and/ or services supplied.</Row>
        <Row num="3.2">Prices quoted are subject to variations occasioned by any increase in the cost of the materials to Project Maintenance Perth subsequent to the date of the quotation.</Row>
        <Row num="3.3">Prices are based on colour selections to mid-range of R.V values. Some colour selections may result in increased prices payable by the Customer following advice by Project Maintenance Perth</Row>
        <Row num="3.4">Project Maintenance Perth reserves the right to implement a surcharge for alterations to specifications of products after the order has been placed.</Row>
        <Row num="3.5">Inadequate Surface Substrate: Prices quoted are for preparation and painting on sound surfaces. Pricing may increase if the surface substrate is faulty or losing its initial integrity. If surface preparation uncovers crumbling plaster or large cracks, additional labour and material costs will be charged.</Row>
      </Section>

      <Section title="4. Acceptance">
        <Row num="4.1">If any instruction is received by Project Maintenance Perth from the Customer for the supply of products and/or services, it shall constitute acceptance of the terms and conditions contained herein. Upon acceptance of these terms and conditions by the Customer, the terms and conditions are definitive and binding.</Row>
        <Row num="4.2">A deposit of 25% of the total quotation is payable upon quote acceptance.</Row>
        <Row num="4.3">In offering to carry out this work, Project Maintenance Perth undertakes to exercise all care in minimising any possible damage to the Customers property.</Row>
        <Row num="4.4">Start dates and completion dates are subject to weather conditions. Project Maintenance Perth reserve the right to postpone any contact due to adverse weather conditions.</Row>
      </Section>

      <Section title="5. Payment, Late Payment, Default of Payment and Consequences of Default of Payment">
        <Row num="5.1"><strong>Electronic Payment Details: </strong>Account Name: <strong>Project Painters Perth Pty Ltd. </strong>BSB: <strong>016281 </strong>Account Number: <strong>289 864 693</strong></Row>
        <Row num="5.2">
          Payment terms – unless stated differently on the front page of this quote, the payment terms are as follows:
          <table className="mt-1 ml-4">
            <tbody>
              <tr><td className="p-0.5 align-top">a.</td><td className="p-0.5">Payment of the account including all additional charges is due in full within 7 days from the completion of the work.</td></tr>
              <tr><td className="p-0.5 align-top">b.</td><td className="p-0.5">Project Maintenance Perth reserves the right to charge interest on any payments not made as above at the rate of 2% per month</td></tr>
              <tr><td className="p-0.5 align-top">c.</td><td className="p-0.5">Project Maintenance Perth may at any time require security for payment and may withhold delivery until provision of sufficient security or Project Maintenance Perth may at its option, suspend or terminate the contract and the payment of all materials delivered and work commenced up to the date of such suspension or termination and all moneys payable hereunder shall be immediately become due and payable</td></tr>
              <tr><td className="p-0.5 align-top">d.</td><td className="p-0.5">All costs and expenses incurred by Project Maintenance Perth in enforcing these conditions of sale including all legal costs incurred in recovering any debts owed to Project Maintenance Perth by the Customer, shall be payable by the Customer.</td></tr>
            </tbody>
          </table>
        </Row>
        <Row num="5.3">In offering to carry out this work, Project Maintenance Perth undertakes to exercise all care in minimising any possible damage to the Customers property.</Row>
        <Row num="5.4">Start dates and completion dates are subject to weather conditions. Project Maintenance Perth reserve the right to postpone any contact due to adverse weather conditions.</Row>
      </Section>

      <Section title="6. Colours">
        <Row num="6.1">Project Maintenance Perth accept no responsibility for choice of colours on any project.</Row>
        <Row num="6.2">If for any reason, the Customer changes their mind after they have chosen a colour, any associated costs will be incurred by the Customer in full. This includes any labour costs during the process.</Row>
        <Row num="6.3">When colour matching is required, Project Maintenance Perth will work closely with our suppliers to achieve the best and closest possible match. There may not always be a 100% match however.</Row>
        <Row num="6.4">Project Maintenance Perth do not accept responsibility for yellowing of any gloss enamel products.</Row>
      </Section>

      <Section title="7. Safety">
        <Row num="7.1">The health, safety and welfare of all persons working on site is the foremost consideration for Project Maintenance Perth. To this end all responsible practical efforts must be made to create and maintain safe working conditions, in conformance with Australian Law</Row>
        <Row num="7.2">When, in the opinion of Project Maintenance Perth, an unsafe condition exists or an unsafe act is being committed (provided such is not caused by Project Maintenance Perth), you will be instructed to take appropriate action to rectify the situation. Failure to do so will result in Project Maintenance Perth applying whatever measures are considered necessary to ensure conformity with the above requirements</Row>
      </Section>

      <Section title="8. Dispute resolution">
        <Row num="">Project Maintenance Perth will endeavour to resolve any dispute between the Customer and itself without the need for Court proceedings. Any such attempt is without legal prejudice</Row>
      </Section>

      <Section title="9. Reservation of title">
        <Row num="">Ownership and title of the goods remains with The Project Maintenance Perth until the purchased price and all other monies owing by the Customer, under the contract or any other contract to Project Maintenance Perth, have been paid in full.</Row>
      </Section>

      <Section title="10. Warranty">
        <Row num="10.1">Project Maintenance Perth offer a five (5) year warranty on workmanship and the products used. All warranties are subject to the Property owner carrying out general maintenance on their property to ensure all work carried out by us is kept to a reasonable standard.</Row>
        <Row num="10.2">Project Maintenance Perth accepts no responsibility for any work carried out by us that is altered, touched up, tampered with by a third party.</Row>
        <Row num="10.3">Any claims made about the standard of workmanship or the carrying out of this work shall be made in writing to Project Maintenance Perth at the address stated on the front page of this quote within 10 days following the completion of the work.</Row>
        <Row num="10.4">If for whatever reason a claim arises due to product failure or workmanship issues we will rectify the issues and we will pay for the paint & products to rectify the issue. The client is required to pay for all the labour costs.</Row>
        <Row num="10.5">Project Maintenance Perth do not warrant substrates that have not been previously sealed with oil pigmented sealer. These substrates include but are not limited to plaster ceilings, walls and cornice. We do not warrant the fading, peeling or blistering of dark colours on external projects, dark colours include reds, blues, greens & blacks.</Row>
        <Row num="10.6">Project Maintenance Perth do not warrant the following issues: cracking, settlement cracking, water stains, rust, peeling ceilings, previously painted surfaces that have not been sealed using oil pigmented sealer.</Row>
        <Row num="10.7">Project Maintenance Perth do not accept responsibility for any damage caused by any other tradespeople whilst painting; any costs associated will be at the cost of the Customer.</Row>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-bold border-b border-gray-200 mb-2 text-[13px]">{title}</div>
      <div className="mb-3">
        <table className="w-full border-collapse">
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ num, children }) {
  return (
    <tr>
      <td className="p-1 align-top w-10 whitespace-nowrap">{num}</td>
      <td className="p-1">{children}</td>
    </tr>
  );
}

export default QuoteAcceptancePage;
